import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, User2, Trophy, Shield, Activity, Radar, Menu, X } from "lucide-react";

const NavItem = ({ to, children, testid, onClick }) => (
  <NavLink
    to={to}
    data-testid={testid}
    onClick={onClick}
    className={({ isActive }) =>
      `text-xs font-bold tracking-[0.2em] uppercase px-3 py-2 transition-colors flex items-center ${
        isActive ? "text-court" : "text-zinc-400 hover:text-white"
      }`
    }
  >
    {children}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [bannerClosed, setBannerClosed] = useState(false);

  const closeBanner = () => {
    setBannerClosed(true);
  };

  // Helper function to cleanly parse out and format the user's name and position abbreviation
  const getUserDisplayName = () => {
    if (!user) return "";
    const firstName = user.name ? user.name.split(" ")[0] : "User";
    
    // Extracts position short code from strings like "Center (C)" or "Point Guard (PG)"
    let positionAbbr = "";
    if (user.position) {
      const match = user.position.match(/\(([^)]+)\)/);
      positionAbbr = match ? match[1] : user.position.slice(0, 2).toUpperCase();
    }

    return positionAbbr ? `${firstName} (${positionAbbr})` : firstName;
  };

  return (
    <div className="min-h-screen bg-obsidian text-white flex flex-col relative">
      {!bannerClosed && (
        <div className="w-full bg-yellow-500 text-black text-sm font-medium py-2">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
            <a
              href="https://forms.gle/BAAxGbxbHnU9AjAD9"
              target="_blank"
              rel="noreferrer"
              className="flex-1"
              data-testid="feedback-banner-link"
            >
              This is a beta version of Court Matiks — please click here to give your feedback.
            </a>
            <button
              onClick={closeBanner}
              aria-label="Close feedback banner"
              className="ml-4 p-1 text-black hover:opacity-80"
              data-testid="feedback-banner-close"
            >
              <span className="sr-only">Close</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
            <img src="/logo.svg" alt="Court Matiks" className="w-7 h-7" />
            <span className="font-display font-black text-xl tracking-tighter uppercase">Court Matiks</span>
          </Link>

          {/* Desktop Only Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/tournaments" testid="nav-tournaments">
              <Trophy className="inline w-3.5 h-3.5 mr-1" />Tournaments
            </NavItem>
            
            <NavItem to="/players/search" testid="nav-players">
              <Radar className="inline w-3.5 h-3.5 mr-1"/>Scout Radar
            </NavItem>

            {user && <NavItem to="/me" testid="nav-profile"><User2 className="inline w-3.5 h-3.5 mr-1" />Profile</NavItem>}
            {user?.role === "admin" && <NavItem to="/admin" testid="nav-admin"><Shield className="inline w-3.5 h-3.5 mr-1" />Admin</NavItem>}
            
            <NavLink
              to="/sandbox/score"
              data-testid="nav-record-game"
              className="ml-2 px-3 py-2 text-xs font-bold tracking-[0.2em] uppercase bg-court text-white border-b-4 border-court-deep hover:bg-court-hover hover:border-court active:border-b-0 active:translate-y-0.5 transition-all flex items-center gap-1"
            >
              <Activity className="w-3.5 h-3.5" /> Record Game
            </NavLink>
          </nav>

          {/* Header Action States Grouping */}
          <div className="flex items-center gap-2">
            
            {/* Desktop Auth States */}
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  <span className="hidden sm:inline text-xs text-zinc-300 font-mono font-bold bg-zinc-900 border border-zinc-800 px-2.5 py-1 rounded">
                    {getUserDisplayName()}
                  </span>
                  <button
                    data-testid="nav-logout-btn"
                    onClick={() => { logout(); nav("/"); }}
                    className="p-2 text-zinc-400 hover:text-white"
                    aria-label="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login-link"
                  className="px-3 py-2 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-700 hover:border-white"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Menu Toggle Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-white focus:outline-none transition-colors rounded-lg hover:bg-zinc-900/50"
              aria-label="Toggle Navigation Menu"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-5 h-5 text-court" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Flyout Navigation Drawer */}
        {isOpen && (
          <div className="md:hidden absolute top-16 left-0 w-full bg-black/95 backdrop-blur-2xl border-b border-white/10 px-4 py-6 flex flex-col gap-4 z-50 transition-all duration-200">
            {/* Display active User Tag inside mobile panel */}
            {user && (
              <div className="px-3 py-2.5 border-b border-white/5 flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-zinc-500 font-mono">
                  Active Asset
                </span>
                <span className="text-sm font-mono font-bold text-court">
                  {getUserDisplayName()}
                </span>
              </div>
            )}

            {/* Vertical Link Stack */}
            <nav className="flex flex-col gap-1.5">
              <NavItem to="/tournaments" testid="nav-tournaments" onClick={() => setIsOpen(false)}>
                <Trophy className="inline w-3.5 h-3.5 mr-2" />Tournaments
              </NavItem>
              
              <NavItem to="/players/search" testid="nav-players" onClick={() => setIsOpen(false)}>
                <Radar className="inline w-3.5 h-3.5 mr-2"/>Scout Radar
              </NavItem>

              {user && (
                <NavItem to="/me" testid="nav-profile" onClick={() => setIsOpen(false)}>
                  <User2 className="inline w-3.5 h-3.5 mr-2" />Profile
                </NavItem>
              )}

              {user?.role === "admin" && (
                <NavItem to="/admin" testid="nav-admin" onClick={() => setIsOpen(false)}>
                  <Shield className="inline w-3.5 h-3.5 mr-2" />Admin
                </NavItem>
              )}
            </nav>

            {/* Mobile Call to Actions & Auth States */}
            <div className="flex flex-col gap-2 pt-4 border-t border-white/5">
              <NavLink
                to="/sandbox/score"
                data-testid="nav-record-game"
                onClick={() => setIsOpen(false)}
                className="w-full justify-center px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase bg-court text-white border-b-4 border-court-deep hover:bg-court-hover hover:border-court active:border-b-0 active:translate-y-0.5 transition-all flex items-center gap-2"
              >
                <Activity className="w-3.5 h-3.5" /> Record Game
              </NavLink>

              {user ? (
                <button
                  data-testid="nav-logout-btn-mobile"
                  onClick={() => {
                    logout();
                    nav("/");
                    setIsOpen(false);
                  }}
                  className="w-full justify-center px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-all flex items-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" /> Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login-link-mobile"
                  onClick={() => setIsOpen(false)}
                  className="w-full text-center px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-700 hover:border-white transition-all"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 text-xs text-zinc-500 font-mono flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Court Matiks</span>          
        </div>
      </footer>
    </div>
  );
}